import { useTranslation } from 'react-i18next';
import { ClickAnchor, ElementsButton, ClickMenuContainer, Icon } from '../../../components';
import { useFeatureFlagsContext } from '../../../../FeatureFlags';
import { displayScorecardElementName, ScorecardElementSymbol } from '../../../../Scorecard';
import { usePresentationViewOptionsContext } from '../context';
import { PresentationViewOptionListItem } from './PresentationViewOptionListItem';
import { PresentationViewOptionsQuickActions } from './PresentationViewOptionsQuickActions';
import { elementShortcutMap, otherShortcutMap } from '../util';
import { useRef, useState } from 'react';
import { useClickAway, useKeyPressEvent } from 'react-use';

const elementOrder: ScorecardElementSymbol[] = ['Sr', 'Br', 'Dr', 'Tr', 'Lt', 'Qt', 'Rt', 'Bt', 'Tt', 'Er', 'Ir', 'Es'];
const elementIcons: Record<ScorecardElementSymbol, Parameters<typeof Icon>[0]['icon']> = {
  Sr: 'srFinancialHealth',
  Br: 'brFinancialHealth',
  Dr: 'drFinancialHealth',
  Tr: 'trFinancialHealth',
  Lt: 'ltFinancialHealth',
  Qt: 'qtFinancialHealth',
  Rt: 'rtFinancialHealth',
  Bt: 'btFinancialHealth',
  Tt: 'ttFinancialHealth',
  Er: 'erFinancialHealth',
  Ir: 'irFinancialHealth',
  Es: 'esFinancialHealth',
};

export function PresentationViewOptions() {
  const {
    viewOptions,
    toggleShowClientAge,
    toggleShowClientName,
    toggleElementVisibility,
    toggleShowFinancialDetails,
    toggleShowEmojis,
    toggleShowTargetScores,
    visibleElements,
  } = usePresentationViewOptionsContext();
  const { t: tPages } = useTranslation('pages');
  const { t: tScorecard } = useTranslation('scorecard');
  const { allowEstateStatus } = useFeatureFlagsContext();

  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  useKeyPressEvent('Escape', () => setViewOptionsOpen(false));
  const viewOptionsRef = useRef<HTMLDivElement>(null);
  useClickAway(viewOptionsRef, () => setViewOptionsOpen(false));
  const visibleElementSet = new Set(visibleElements);
  const visibleElementOptions = elementOrder.filter(
    (symbol) => visibleElementSet.has(symbol) && (symbol !== 'Es' || allowEstateStatus === true)
  );

  return (
    <ClickAnchor
      placement="bottom-end"
      popperJSPreventOverflowOptions={{ altAxis: true, padding: 24 }}
      floatingElement={
        viewOptionsOpen ? (
          <div ref={viewOptionsRef}>
            <ClickMenuContainer className="!w-[300px] overflow-y-scroll max-h-[calc(100vh_-_48px)]">
              <PresentationViewOptionsQuickActions />
              {visibleElementOptions.map((symbol, index) => (
                <PresentationViewOptionListItem
                  key={symbol}
                  shortcutCharacter={elementShortcutMap[symbol].toUpperCase()}
                  className={index === visibleElementOptions.length - 1 ? 'mb-8' : undefined}
                  label={displayScorecardElementName(symbol, tScorecard)}
                  prefix={<Icon className="mr-3 !h-6 !w-6" icon={elementIcons[symbol]} />}
                  onClick={() => toggleElementVisibility(symbol)}
                  icon={viewOptions?.focusedElements?.includes(symbol) && 'check'}
                />
              ))}
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleEmojis'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowEmojis}
                icon={viewOptions?.showEmojis ? 'hide' : 'show'}
                label={
                  viewOptions?.showEmojis
                    ? tPages('full-screen-scorecard-view-options-hide-emojis')
                    : tPages('full-screen-scorecard-view-options-show-emojis')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleTargetScores'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowTargetScores}
                icon={viewOptions?.showTargetScores ? 'hide' : 'show'}
                label={
                  viewOptions?.showTargetScores
                    ? tPages('full-screen-scorecard-view-options-hide-target-ranges')
                    : tPages('full-screen-scorecard-view-options-show-target-ranges')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleClientName'].toUpperCase()}
                onClick={toggleShowClientName}
                className="[&_p]:font-medium"
                icon={viewOptions?.showClientName ? 'hide' : 'show'}
                label={
                  viewOptions?.showClientName
                    ? tPages('full-screen-scorecard-view-options-hide-client-name')
                    : tPages('full-screen-scorecard-view-options-show-client-name')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleClientAge'].toUpperCase()}
                onClick={toggleShowClientAge}
                className="[&_p]:font-medium"
                icon={viewOptions?.showClientAge ? 'hide' : 'show'}
                label={
                  viewOptions?.showClientAge
                    ? tPages('full-screen-scorecard-view-options-hide-client-age')
                    : tPages('full-screen-scorecard-view-options-show-client-age')
                }
              />
              <PresentationViewOptionListItem
                shortcutCharacter={otherShortcutMap['toggleFinancialDetails'].toUpperCase()}
                className="[&_p]:font-medium"
                onClick={toggleShowFinancialDetails}
                icon={viewOptions?.showFinancialDetails ? 'hide' : 'show'}
                label={
                  viewOptions?.showFinancialDetails
                    ? tPages('full-screen-scorecard-view-options-hide-financial-details')
                    : tPages('full-screen-scorecard-view-options-show-financial-details')
                }
              />
            </ClickMenuContainer>
          </div>
        ) : (
          <ClickMenuContainer></ClickMenuContainer>
        )
      }
    >
      <ElementsButton
        size="small"
        theme="text"
        icon="arrowDropdown"
        label={tPages('full-screen-scorecard-view-options-button')}
        onClick={() => setViewOptionsOpen(!viewOptionsOpen)}
      />
    </ClickAnchor>
  );
}
